/**
 * PaymentMethodsModal - Modal de gestion des méthodes de paiement
 * 
 * Affiche les cartes bancaires enregistrées avec Stripe.
 * Permet accès au Customer Portal pour gestion complète.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import StripeService from '../../services/api/stripeService';

interface PaymentMethodsModalProps {
    id: string;
    companyId: string;
    accessToken: string;
    onClose?: () => void;
}

const PaymentMethodsModal: React.FC<PaymentMethodsModalProps> = ({
    id,
    companyId,
    accessToken,
    onClose
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    
    const [loading, setLoading] = useState(true);
    const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string | null>(null);
    const [portalLoading, setPortalLoading] = useState(false);

    // Charger les payment methods depuis le backend
    useEffect(() => {
        const loadPaymentMethods = async () => {
            setLoading(true);
            try {
                const response = await StripeService.getPaymentMethods(companyId, accessToken);
                if (response.success && response.data) {
                    setPaymentMethods(response.data.payment_methods || []);
                    setDefaultPaymentMethod(response.data.default_payment_method || null);
                } else {
                    console.error('[PaymentMethodsModal] Failed to load payment methods:', response);
                    setPaymentMethods([]);
                }
            } catch (error) {
                console.error('[PaymentMethodsModal] Error loading payment methods:', error);
                setPaymentMethods([]);
            } finally {
                setLoading(false);
            }
        };

        loadPaymentMethods();
    }, [companyId, accessToken]);

    const handleOpenPortal = async () => {
        setPortalLoading(true);
        try {
            const returnUrl = `${window.location.origin}/dashboard/home`;
            const response = await StripeService.createPortalSession(companyId, returnUrl, accessToken);
            
            if (response.success && response.data?.portal_url) {
                // Rediriger vers Stripe Customer Portal
                window.location.href = response.data.portal_url;
            } else {
                const errorMsg = StripeService.formatErrorMessage(response);
                alert(`Erreur lors de l'ouverture du portail: ${errorMsg}`);
                setPortalLoading(false);
            }
        } catch (error) {
            console.error('[PaymentMethodsModal] Portal error:', error);
            alert('Erreur lors de l\'ouverture du portail Stripe.');
            setPortalLoading(false);
        }
    };

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const getCardBrandIcon = (brand: string): string => {
        const brandMap: Record<string, string> = {
            'visa': 'fa-cc-visa',
            'mastercard': 'fa-cc-mastercard',
            'amex': 'fa-cc-amex',
            'discover': 'fa-cc-discover',
            'diners': 'fa-cc-diners-club',
            'jcb': 'fa-cc-jcb'
        };
        return brandMap[brand.toLowerCase()] || 'fa-credit-card';
    };

    const getCardBrandColor = (brand: string): string => {
        const colorMap: Record<string, string> = {
            'visa': '#1A1F71',
            'mastercard': '#EB001B',
            'amex': '#006FCF',
            'discover': '#FF6000',
            'diners': '#0079BE',
            'jcb': '#0E4C96'
        };
        return colorMap[brand.toLowerCase()] || '#6c757d';
    };

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', borderRadius: '15px' }}>
                    <div className="modal-header" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            <i className="fa-solid fa-credit-card me-2"></i>
                            {t('views.modal.paymentMethods.title', 'Méthodes de paiement')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body p-4">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">{t('views.modal.paymentMethods.loading', 'Chargement des méthodes de paiement...')}</p>
                            </div>
                        ) : paymentMethods.length > 0 ? (
                            <>
                                <div className="row g-3">
                                    {paymentMethods.map((pm: any) => {
                                        const isDefault = pm.id === defaultPaymentMethod;
                                        return (
                                            <div key={pm.id} className="col-12">
                                                <div 
                                                    className={`card ${isDefault ? 'border-success border-2' : ''}`}
                                                    style={{
                                                        borderRadius: '12px',
                                                        background: isDefault ? 'linear-gradient(135deg, rgba(40, 167, 69, 0.15), rgba(32, 201, 151, 0.08))' : undefined
                                                    }}
                                                >
                                                    <div className="card-body p-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center">
                                                                {/* Icône carte */}
                                                                <div 
                                                                    className="d-flex align-items-center justify-content-center me-3"
                                                                    style={{
                                                                        width: '60px',
                                                                        height: '40px',
                                                                        borderRadius: '8px',
                                                                        background: `linear-gradient(135deg, ${getCardBrandColor(pm.card.brand)}, ${getCardBrandColor(pm.card.brand)}dd)`,
                                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                                                    }}
                                                                >
                                                                    <i className={`fa-brands ${getCardBrandIcon(pm.card.brand)} text-white fs-4`}></i>
                                                                </div>
                                                                
                                                                {/* Détails carte */}
                                                                <div>
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <strong className="text-capitalize me-2 fw-bold" style={{ fontSize: '1.05rem' }}>
                                                                            {pm.card.brand}
                                                                        </strong>
                                                                        <span style={{ 
                                                                            fontSize: '1rem',
                                                                            fontWeight: 500,
                                                                            opacity: 0.85
                                                                        }}>
                                                                            •••• {pm.card.last4}
                                                                        </span>
                                                                    </div>
                                                                    <small style={{ opacity: 0.7, fontSize: '0.875rem' }}>
                                                                        <i className="fa-solid fa-calendar-alt me-1"></i>
                                                                        {t('views.modal.paymentMethods.expires', 'Expire')}: {String(pm.card.exp_month).padStart(2, '0')}/{pm.card.exp_year}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Badge par défaut */}
                                                            {isDefault && (
                                                                <span className="badge bg-success px-3 py-2" style={{ fontSize: '0.85rem' }}>
                                                                    <i className="fa-solid fa-check-circle me-1"></i>
                                                                    {t('views.modal.paymentMethods.default', 'Par défaut')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Info sur gestion via Portal */}
                                <div 
                                    className="alert mt-4 mb-0 d-flex align-items-start"
                                    style={{
                                        backgroundColor: 'rgba(13, 202, 240, 0.1)',
                                        border: '1px solid rgba(13, 202, 240, 0.3)',
                                        borderRadius: '10px'
                                    }}
                                >
                                    <i className="fa-solid fa-info-circle me-3 mt-1" style={{ color: '#0dcaf0', fontSize: '1.2rem' }}></i>
                                    <div>
                                        <h6 className="mb-2 fw-bold">
                                            {t('views.modal.paymentMethods.manageInfo', 'Gestion complète')}
                                        </h6>
                                        <p className="mb-0 small" style={{ opacity: 0.85 }}>
                                            {t('views.modal.paymentMethods.manageDescription', 
                                                'Pour ajouter, supprimer ou modifier vos méthodes de paiement, utilisez le portail sécurisé Stripe en cliquant sur "Gérer via Stripe Portal".')}
                                        </p>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <i className="fa-solid fa-credit-card mb-3" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                                <p className="mb-4">
                                    {t('views.modal.paymentMethods.empty', 'Aucune méthode de paiement enregistrée')}
                                </p>
                                <p className="small" style={{ opacity: 0.7 }}>
                                    {t('views.modal.paymentMethods.emptyDescription', 
                                        'Ajoutez une méthode de paiement via le portail Stripe pour gérer vos paiements.')}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                            disabled={portalLoading}
                        >
                            <i className="fa-solid fa-times me-2"></i>
                            {t('words.close', 'Fermer')}
                        </button>
                        <button
                            type="button"
                            onClick={handleOpenPortal}
                            className="btn btn-primary"
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                            disabled={portalLoading}
                        >
                            {portalLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {t('views.modal.paymentMethods.opening', 'Ouverture...')}
                                </>
                            ) : (
                                <>
                                    <i className="fa-brands fa-stripe me-2"></i>
                                    {t('views.modal.paymentMethods.openPortal', 'Gérer via Stripe Portal')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default PaymentMethodsModal;
